// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { useAccountsPositions } from '@shared/advisory/aggregation/hooks/use-accounts-positions';
import type {
  InternalProposalOperation,
  InternalTargetPortfolioPosition,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import {
  buildOperationSourceKeys,
  findOperationSourceAsset,
  operationSourceIbans,
} from '@shared/data-access/transformers/proposals/operation-source-positions';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

export interface UseOperationSourcePositionsProps {
  readonly clientId?: number | null;
  readonly operations: readonly InternalProposalOperation[];
  /** IBANs already covered by the advisory proposal perimeter. */
  readonly scopedIbans: readonly string[];
  readonly currency: AccountCurrency;
  readonly skip?: boolean;
}

/**
 * Positions the operations sell or transfer away from accounts outside the advisory
 * proposal perimeter.
 *
 * The simulation rejects an operation whose source position is missing from
 * `current_portfolio`, which is what used to limit transfers to the accounts of the
 * proposal. The rest of those accounts stays out of the proposal portfolio.
 */
export const useOperationSourcePositions = ({
  clientId,
  operations,
  scopedIbans,
  currency,
  skip,
}: UseOperationSourcePositionsProps) => {
  const sourceKeys = useMemo(
    () => buildOperationSourceKeys(operations, scopedIbans),
    [operations, scopedIbans],
  );

  const ibans = useMemo(() => operationSourceIbans(sourceKeys), [sourceKeys]);

  const { accountsPositionsQuery, ...accountsPositions } = useAccountsPositions({
    ibans,
    clientId,
    skip: skip || ibans.length === 0,
    institutionId: 'all',
    currency,
  });

  const positions = useMemo<InternalTargetPortfolioPosition[]>(
    () =>
      accountsPositions.holdings.flatMap(holding => {
        const sourceAsset = findOperationSourceAsset(sourceKeys, {
          iban: holding.iban,
          identifiers: [
            holding.details?.ticker,
            holding.details?.isin,
            holding.ticker,
            holding.isin,
          ],
        });

        if (!sourceAsset) {
          return [];
        }

        // The operation's own ticker and ISIN: what the simulation matches on.
        return [
          {
            iban: holding.iban!,
            ticker: sourceAsset.ticker,
            isin: sourceAsset.isin,
            quantity: holding.quantity,
            amount: holding.investment,
            institution_id: holding.institution_id!,
          },
        ];
      }),
    [accountsPositions.holdings, sourceKeys],
  );

  return {
    positions,
    isLoading:
      accountsPositionsQuery.isLoading || accountsPositionsQuery.isFetching,
  } as const;
};
